import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  Button,
  Grid,
  Box,
  IconButton,
  DialogActions,
  Divider,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { IUserAddress } from '../../../utils/entities/interface';
import { AddOrEditUserAddress } from '../../../services/users';
import useSnackbar from '../../../hooks/useSnackbar';

const EditAddressDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  exisitingAddress?: IUserAddress | null;
  onSave: (address: IUserAddress) => void;
  userId: string;
}> = ({ open, onClose, exisitingAddress, onSave, userId }) => {
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [address, setAddress] = useState<IUserAddress>({
    _id: '',
    userId: '',
    house: '',
    street: '',
    city: '',
    state: '',
    saveAs: 'HOME',
    landmark: '',
    zipcode: '',
    createdAt: '',
    updatedAt: '',
  });

  useEffect(() => {
    if (exisitingAddress) {
      setAddress(exisitingAddress);
    } else {
      setAddress({
        _id: '',
        userId: '',
        house: '',
        street: '',
        city: '',
        state: '',
        saveAs: 'HOME',
        landmark: '',
        zipcode: '',
        createdAt: '',
        updatedAt: '',
      });
    }
  }, [exisitingAddress]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress({ ...address, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (
      !address.city ||
      !address.state ||
      !address.street ||
      !address.zipcode ||
      !address.saveAs ||
      !address.landmark
    ) {
      showSnackbar('All fields are required', 'error');
      return;
    }

    const { status, data } = await AddOrEditUserAddress({
      userId: address.userId ? address.userId : userId,
      houseNumber: address.house,
      street: address.street,
      state: address.state,
      city: address.city,
      zipCode: address.zipcode,
      addressId: address._id,
      landmark: address.landmark,
      saveAs: address.saveAs,
    });

    if (status == 201) {
      showSnackbar('User Address Added Success', 'success');
      setTimeout(() => {
        onSave(data);
        onClose();
      }, 1000);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        sx={{ borderRadius: 10, overflow: 'hidden' }} // Ensuring rounded corners
        PaperProps={{
          sx: {
            borderRadius: 10,
            overflow: 'hidden', // Ensures the background doesn't leak
          },
        }}
      >
        {' '}
        <Box
          sx={{
            border: '1px solid #7B1FA2', // Add border here
            borderRadius: 10,
            overflow: 'hidden', // Prevent border from affecting layout
          }}
        >
          {' '}
          <DialogTitle
            sx={{
              fontSize: '22px',
              fontWeight: 'bold',
              color: '#fff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#181818',
              padding: '16px',
            }}
          >
            {exisitingAddress ? 'Edit Address' : 'Add New Address'}
            <IconButton onClick={onClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ backgroundColor: '#181818', padding: '24px' }}>
            <Grid container spacing={2}>
              <Grid item xs={6} mt={2}>
                <Typography gutterBottom>House Number</Typography>
                <TextField
                  fullWidth
                  name="house"
                  variant="outlined"
                  value={address.house}
                  onChange={handleChange}
                  InputLabelProps={{ style: { color: 'white' } }}
                  sx={{
                    backgroundColor: '#222',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
              </Grid>

              <Grid item xs={6} mt={2}>
                <Typography gutterBottom>
                  {' '}
                  Street <span style={{ color: '#7B1FA2' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  name="street"
                  variant="outlined"
                  value={address.street}
                  onChange={handleChange}
                  InputLabelProps={{ style: { color: 'white' } }}
                  sx={{
                    backgroundColor: '#222',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography gutterBottom>
                  {' '}
                  City <span style={{ color: '#7B1FA2' }}>*</span>
                </Typography>
                <TextField
                  name="city"
                  fullWidth
                  variant="outlined"
                  value={address.city}
                  onChange={handleChange}
                  InputLabelProps={{ style: { color: 'white' } }}
                  sx={{
                    backgroundColor: '#222',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography gutterBottom>
                  {' '}
                  Landmark <span style={{ color: '#7B1FA2' }}>*</span>
                </Typography>
                <TextField
                  name="landmark"
                  fullWidth
                  variant="outlined"
                  value={address.landmark}
                  onChange={handleChange}
                  InputLabelProps={{ style: { color: 'white' } }}
                  sx={{
                    backgroundColor: '#222',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography gutterBottom>
                  {' '}
                  State <span style={{ color: '#7B1FA2' }}>*</span>
                </Typography>
                <TextField
                  name="state"
                  fullWidth
                  variant="outlined"
                  value={address.state}
                  onChange={handleChange}
                  InputLabelProps={{ style: { color: 'white' } }}
                  sx={{
                    backgroundColor: '#222',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography gutterBottom>
                  {' '}
                  Save As <span style={{ color: '#7B1FA2' }}>*</span>
                </Typography>
                <TextField
                  name="saveAs"
                  select
                  fullWidth
                  variant="outlined"
                  value={address.saveAs}
                  onChange={handleChange}
                  InputLabelProps={{ style: { color: 'white' } }}
                  sx={{
                    backgroundColor: '#222',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                >
                  <MenuItem value="HOME">Home</MenuItem>
                  <MenuItem value="WORK">Office</MenuItem>
                  <MenuItem value="FRIEND">Friend</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Typography gutterBottom>
                  {' '}
                  Zip Code <span style={{ color: '#7B1FA2' }}>*</span>
                </Typography>
                <TextField
                  name="zipcode"
                  fullWidth
                  // label="Zip Code"
                  variant="outlined"
                  value={address.zipcode}
                  onChange={handleChange}
                  InputLabelProps={{ style: { color: 'white' } }}
                  sx={{
                    backgroundColor: '#222',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
              </Grid>
            </Grid>
            <DialogActions sx={{ backgroundColor: '#181818', padding: '16px' }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ color: '#bbb', borderColor: '#444' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  backgroundColor: '#7B1FA2',
                  '&:hover': { backgroundColor: '#5E167A' },
                }}
              >
                {exisitingAddress ? 'Save' : 'Save'}
              </Button>
            </DialogActions>
          </DialogContent>
        </Box>
      </Dialog>

      {SnackbarComponent}
    </>
  );
};

export default EditAddressDialog;
